import { Controller, useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatDateSafe } from '@/utils/dateUtils';
import type { VacationSegment, SegmentDecision } from '../../../models/vacation.types';
import type { DpDecisionFormData } from '../../../models/vacation.schemas';

interface SegmentDecisionControlProps {
    segment: VacationSegment;
    previousDecision?: SegmentDecision | null;
}

export function SegmentDecisionControl({ segment, previousDecision }: SegmentDecisionControlProps) {
    const { control, watch } = useFormContext<DpDecisionFormData>();
    const fieldName = `segmentDecisions.${segment.order}` as const;
    const decision = watch(`${fieldName}.decision` as any);

    if (previousDecision?.status === 'Approved') {
        return (
            <p className="text-sm font-medium text-emerald-700 font-montserrat">
                Validado{previousDecision.at ? ` em ${formatDateSafe(previousDecision.at.toDate())}` : ''}
            </p>
        );
    }

    return (
        <div className="space-y-2">
            <p className="text-xs font-medium text-raro-gray-500 font-montserrat">Sua decisão</p>
            <Controller
                name={`${fieldName}.decision` as any}
                control={control}
                render={({ field }) => (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => field.onChange('approve')}
                            className={clsx(
                                'flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors font-montserrat',
                                decision === 'approve'
                                    ? 'bg-emerald-600 text-white'
                                    : 'border border-raro-gray-200 bg-white text-raro-gray-700 hover:border-raro-gray-300'
                            )}
                        >
                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            Validar
                        </button>
                        <button
                            type="button"
                            onClick={() => field.onChange('reject')}
                            className={clsx(
                                'flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors font-montserrat',
                                decision === 'reject'
                                    ? 'bg-red-600 text-white'
                                    : 'border border-raro-gray-200 bg-white text-raro-gray-700 hover:border-raro-gray-300'
                            )}
                        >
                            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                            Reprovar
                        </button>
                    </div>
                )}
            />

            {decision === 'reject' && (
                <Controller
                    name={`${fieldName}.note` as any}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <div>
                            <textarea
                                {...field}
                                placeholder="Motivo (mínimo 10 caracteres)"
                                rows={2}
                                className={clsx(
                                    'w-full rounded-xl border p-2 text-sm font-montserrat focus:border-primary-500 focus:ring-primary-500',
                                    error ? 'border-red-300 bg-red-50' : 'border-raro-gray-200'
                                )}
                            />
                            {error && (
                                <p className="mt-1 text-xs text-red-600 font-montserrat">{error.message}</p>
                            )}
                        </div>
                    )}
                />
            )}
        </div>
    );
}
